import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import utils from '../../utils/utils';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import Loader from '../../components/loader';
import ErrorMessage from '../../components/errorMessage';
import { useDeliveryOrderBatches, useEditDeliveryDayMarketplace } from './apiHooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinusCircle, faUndo } from '@fortawesome/free-solid-svg-icons';

const PackerDeliveryDayMarketplaceRefunds = ({ deliveryDayMarketplace }) => {
  const [unfulfilledOrderItems, setUnfulfilledOrderItems] = useState([]);

  const {
    editDeliveryDayMarketplace,
    isLoading: editLoading,
    isError: isEditError,
    error: editError,
  } = useEditDeliveryDayMarketplace(() => {});

  const itemsNeededToRefund = deliveryDayMarketplace.combinedOrderItems
    .filter((coi) => coi.orderedQuantity > coi.fulfilledQuantity)
    .map((coi) => ({
      quantity: coi.orderedQuantity - coi.fulfilledQuantity,
      item: coi.item,
    }));

  const {
    deliveryOrderBatches,
    isLoading: deliveryOrderBatchesLoading,
    isError: isDeliveryOrderBatchesError,
    error: deliveryOrderBatchesError,
  } = useDeliveryOrderBatches({
    deliveryDayId: deliveryDayMarketplace.deliveryDayId,
    itemIds: itemsNeededToRefund.map((refundItem) => refundItem.item.id).join(','),
  });

  useEffect(() => {
    if (!deliveryOrderBatches) {
      return;
    }

    const newUnfulfilledOrderItems = deliveryOrderBatches.reduce((result, dob) => {
      return result.concat(
        dob.orders.reduce((result, order) => {
          return result.concat(
            order.items.reduce((result, oi) => {
              if (oi.fulfilledQuantity != null) {
                return result.concat(oi);
              }

              return result;
            }, [])
          );
        }, [])
      );
    }, []);

    setUnfulfilledOrderItems(newUnfulfilledOrderItems);
  }, [deliveryOrderBatches]);

  const setOrderItemFulfilledQuantity = (orderItem, newQuantity, index) => {
    const newUnfulfilledOrderItems = [...unfulfilledOrderItems];

    if (index >= 0) {
      newUnfulfilledOrderItems[index].fulfilledQuantity = newQuantity;
    } else {
      newUnfulfilledOrderItems.push({ ...orderItem, fulfilledQuantity: newQuantity });
    }

    setUnfulfilledOrderItems(newUnfulfilledOrderItems);
  };

  const getUnfulfilledCountForRefundItem = (refundItem) => {
    return unfulfilledOrderItems.reduce((previousValue, uoi) => {
      if (uoi.item.id === refundItem.item.id) {
        return previousValue + uoi.quantity - uoi.fulfilledQuantity;
      }

      return previousValue;
    }, 0);
  };

  const { formatMessage } = useIntl();

  return (
    <div>
      <Loader show={editLoading || deliveryOrderBatchesLoading} />
      {isEditError || isDeliveryOrderBatchesError ? (
        <ErrorMessage error={utils.getErrorMessageFromResponse(editError || deliveryOrderBatchesError)} />
      ) : (
        ''
      )}

      {deliveryDayMarketplace.combinedOrderItems?.length ? (
        <div className="row m-b-6">
          <div className="col-12 col-sm-12 col-md-10 col-lg-8 m-b-6">
            <h3>
              <FormattedMessage id={'packer.delivery.day.marketplace.inventory.refunds.list.title'} />
            </h3>
            <table className="table table-striped">
              <thead>
                <tr>
                  <th scope="col">
                    <FormattedMessage id={'packer.delivery.day.marketplace.inventory.refunds.table.header.item'} />
                  </th>
                  <th scope="col">
                    <FormattedMessage id={'packer.delivery.day.marketplace.inventory.refunds.table.header.quantity'} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {itemsNeededToRefund.map((refundItem, index) => {
                  const refundCount = getUnfulfilledCountForRefundItem(refundItem);

                  return (
                    <tr key={index}>
                      <td>
                        <Link to={`/item/${refundItem.item.id}`}>
                          #{refundItem.item.id} {utils.getItemName(refundItem.item, formatMessage)}
                        </Link>
                      </td>
                      <td>
                        {refundCount} / {refundItem.quantity}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="col-12 col-sm-12 col-md-10 col-lg-8 m-b-6">
            <h3>
              <FormattedMessage id={'packer.delivery.day.marketplace.inventory.refunds.orders.title'} />
            </h3>
            {deliveryDayMarketplace && !deliveryDayMarketplace.orderItemsFulfilled ? (
              <div className="m-b-3">
                <button
                  className={`btn btn-success m-r-2 ${editLoading ? 'btn-loading' : ''}`}
                  disabled={itemsNeededToRefund.some((refundItem) => {
                    const refundCount = getUnfulfilledCountForRefundItem(refundItem);

                    return refundCount !== refundItem.quantity;
                  })}
                  onClick={() => {
                    editDeliveryDayMarketplace({
                      populateFulfilledQuantities: true,
                      deliveryDayMarketplace: {
                        ...deliveryDayMarketplace,
                        unfulfilledOrderItems,
                      },
                    });
                  }}
                >
                  <FormattedMessage id={'packer.delivery.day.marketplace.inventory.cta.complete'} />
                </button>
              </div>
            ) : (
              ''
            )}
            <table className="table table-striped">
              <thead>
                <tr>
                  <th scope="col">
                    <FormattedMessage
                      id={'packer.delivery.day.marketplace.inventory.refunds.orders.table.header.customer'}
                    />
                  </th>
                  <th scope="col">
                    <FormattedMessage
                      id={'packer.delivery.day.marketplace.inventory.refunds.orders.table.header.items'}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {deliveryOrderBatches ? (
                  deliveryOrderBatches.map((dob, index) => {
                    return (
                      <tr key={index}>
                        <td>
                          <div>
                            {dob.packingId ? `#${dob.packingId}` : `D${dob.id}`} - {dob.user.screenName}
                          </div>
                        </td>
                        <td>
                          {dob.orders.map((order) => {
                            return (
                              <div key={order.id} className="m-b-6">
                                <h5 className="m-b-1">{order.marketplaceName}</h5>
                                {order.items.map((oi) => {
                                  const unfulfilledIndex = unfulfilledOrderItems.findIndex((uoi) => uoi.id === oi.id);
                                  const quantity =
                                    unfulfilledIndex >= 0
                                      ? unfulfilledOrderItems[unfulfilledIndex].fulfilledQuantity
                                      : oi.quantity;

                                  const refundItem = itemsNeededToRefund.find(
                                    (refundItem) => refundItem.item.id === oi.item.id
                                  );

                                  const refundCount = unfulfilledOrderItems.reduce((previousValue, uoi) => {
                                    if (uoi.item.id === oi.item.id) {
                                      return previousValue + uoi.quantity - uoi.fulfilledQuantity;
                                    }

                                    return previousValue;
                                  }, 0);

                                  const canRefund =
                                    refundItem &&
                                    refundCount < refundItem.quantity &&
                                    !deliveryDayMarketplace.orderItemsFulfilled;

                                  return (
                                    <div key={oi.id} className={refundItem ? 'm-b-2' : ''}>
                                      <span className="m-r-2">{oi.itemName}</span>
                                      {refundItem ? (
                                        <>
                                          {canRefund ? (
                                            <FontAwesomeIcon
                                              size="lg"
                                              onClick={() =>
                                                quantity > 0
                                                  ? setOrderItemFulfilledQuantity(oi, quantity - 1, unfulfilledIndex)
                                                  : 0
                                              }
                                              className="m-r-1"
                                              icon={faMinusCircle}
                                            />
                                          ) : (
                                            ''
                                          )}
                                          <input
                                            disabled={deliveryDayMarketplace.orderItemsFulfilled}
                                            value={quantity}
                                            className={`form-control d-inline-block packer__delivery-day-marketplace-combined-order-item-refunds-input ${
                                              quantity < oi.quantity ? 'text-danger' : ''
                                            }`}
                                            type="number"
                                            onChange={(e) =>
                                              setOrderItemFulfilledQuantity(oi, e.target.value, unfulfilledIndex)
                                            }
                                          />
                                          {!deliveryDayMarketplace.orderItemsFulfilled ? (
                                            <>
                                              <FontAwesomeIcon
                                                onClick={() =>
                                                  setOrderItemFulfilledQuantity(oi, oi.quantity, unfulfilledIndex)
                                                }
                                                className="m-l-1 m-r-1"
                                                icon={faUndo}
                                              />
                                              <small>{oi.quantity}</small>
                                            </>
                                          ) : (
                                            ''
                                          )}
                                        </>
                                      ) : (
                                        ''
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr />
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

PackerDeliveryDayMarketplaceRefunds.propTypes = {
  currentUser: PropTypes.object,
  deliveryDayMarketplace: PropTypes.object.isRequired,
};

PackerDeliveryDayMarketplaceRefunds.defaultProps = {};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser.user,
  };
}

export default injectIntl(connect(mapStateToProps)(PackerDeliveryDayMarketplaceRefunds));
